import {
  IconArrowOutOfBox,
  IconWorld,
  Button,
  Link
} from '@sequencehq/core-components'
import {
  ExecutionSummaryComponent,
  ExecutionSummaryComponentProps
} from 'Automations/nodeDefinitions/types'
import {
  NodeTimeline,
  QuotePreviewCard,
  useNodeTimelineVariant
} from 'Automations/components'
import {
  CancelledItem,
  FailedItem,
  hasAllTasksInProgress,
  hasCancelledTask,
  hasFailedTask,
  TerminalStateItem
} from 'Automations/nodeDefinitions/executionSummaryUtils'
import { useMemo } from 'react'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useAllUsers } from 'common/hooks/useUsers'
import { TASK_EXECUTORS } from 'Automations/automations.constants'

export const PublishQuoteExecutionSummary: ExecutionSummaryComponent = ({
  tasks,
  automationExecution,
  isLatestNodeExecution
}) => {
  const showPreviews = useNodeTimelineVariant() === 'DEFAULT'
  const {
    tasksInProgress,
    taskFailed,
    taskCancelled,
    quote,
    customer,
    requestorEmail
  } = usePublishQuoteData({
    tasks,
    automationExecution
  })

  if (taskCancelled) {
    return (
      <CancelledItem
        label="Quote publishing cancelled"
        timestamp={automationExecution.createTime}
        isLatestNodeExecution={isLatestNodeExecution}
      />
    )
  }

  if (tasksInProgress) {
    return (
      <TerminalStateItem
        icon={<IconArrowOutOfBox />}
        label="Quote is being published..."
        timestamp={automationExecution.createTime}
        isLatestNodeExecution={isLatestNodeExecution}
      />
    )
  }

  if (taskFailed) {
    return (
      <FailedItem
        label="Quote publishing failed"
        timestamp={automationExecution.createTime}
        isLatestNodeExecution={isLatestNodeExecution}
      />
    )
  }

  return (
    <>
      <NodeTimeline.Item icon={<IconWorld />} showStem={!isLatestNodeExecution}>
        <NodeTimeline.Title>
          <NodeTimeline.UserAction
            label="Quote published"
            performedBy={requestorEmail}
            timestamp={automationExecution.createTime}
          />
        </NodeTimeline.Title>
        {showPreviews && !!(quote && customer) && (
          <QuotePreviewCard
            quote={quote}
            customer={customer}
            startCollapsed={!isLatestNodeExecution}
            action={
              <Button
                data-testid="watchtower.publishQuote.viewQuote"
                to={`/quotes/${quote.id}`}
                as={Link.Unstyled}
                variant="secondary"
                fullWidth
              >
                View quote
              </Button>
            }
          />
        )}
      </NodeTimeline.Item>
    </>
  )
}

const usePublishQuoteData = ({
  tasks,
  automationExecution
}: {
  tasks: ExecutionSummaryComponentProps['tasks']
  automationExecution: ExecutionSummaryComponentProps['automationExecution']
}) => {
  const { users } = useAllUsers()
  const tasksInProgress = useMemo(() => hasAllTasksInProgress(tasks), [tasks])

  const publishQuoteReviewTask = useMemo(() => {
    return tasks.find(
      task =>
        task.inputData.taskExecutorConfiguration?.executor ===
        TASK_EXECUTORS.publishQuote
    )
  }, [tasks])

  const taskFailed = useMemo(() => hasFailedTask(tasks), [tasks])

  const taskCancelled = useMemo(() => hasCancelledTask(tasks), [tasks])

  const { quoteId } = useMemo(() => {
    return {
      quoteId: publishQuoteReviewTask?.inputData.data.quoteId as
        | string
        | undefined
    }
  }, [publishQuoteReviewTask])

  const quoteQuery = useQuery(
    dashboard20240730Client.getQuote,
    {
      id: quoteId ?? ''
    },
    {
      enabled: !!quoteId
    }
  )
  const customerQuery = useQuery(
    dashboard20240730Client.getCustomer,
    {
      id: quoteQuery.data?.customerId ?? ''
    },
    {
      enabled: !!quoteQuery.data?.customerId
    }
  )

  const requestorEmail = useMemo(() => {
    const requestorId = automationExecution.input.requestorId
    if (!requestorId) return ''
    return users.find(user => user.id === requestorId)?.email || 'Unknown user'
  }, [users, automationExecution.input.requestorId])

  const quote = quoteQuery.data ?? undefined
  const customer = customerQuery.data ?? undefined

  return {
    tasksInProgress,
    taskFailed,
    taskCancelled,
    quote,
    customer,
    requestorEmail
  }
}
